import React, { useEffect, useState, useCallback, useMemo } from "react";
import { connect, useSelector } from "react-redux";
import TodosTable from "./TodosTable";
import "./index.css";
import { getUsers } from "../../services/auth";
import SelectField from "../Shared/SelectField";
import { getUsersWithViewableTodos } from "../../utils";
import { ADMIN_ROLE, SCOPE } from "../../constants";
import { deleteTodo, getTodos } from "../../services/todos";
import WorkflowsLoading from "../Workflow/components/WorkflowsLoading";
import useOtherClientAppointments from "../Workflow/useOtherClientAppointments";
import _ from "lodash";

const mapStateToProps = (state) => ({
  historyClientAppointments: state.workflow.historyClientAppointments,
  archivedClientAppointments: state.workflow.archivedClientAppointments,
  clientAppointments: state.workflow.clientAppointments,
  state: state,
});
const MyTodoList = ({
  historyClientAppointments = [],
  archivedClientAppointments = [],
  clientAppointments = [],
  state,
}) => {
  // Each todo links to its workflow's tab, which needs ARCHIVED and HISTORY.
  const isOtherClientAppointmentsLoaded = useOtherClientAppointments();
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [todos, setTodos] = useState([]);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [specialtyFilter, setSpecialtyFilter] = useState([]);

  const currentUser = useSelector((state) => state.user?.currentUser);
  const practitionerProfiles = useSelector((state) => state.app.specialistProfiles);
  const [assigneeId, setAssigneeId] = useState();
  useEffect(() => {
    setAssigneeId(currentUser?.uid);
  }, [currentUser]);

  const handleSelectAssignee = (e) => setAssigneeId(e.target.value);

  const fetchTodos = useCallback(
    async (assigned_to) => {
      const result = await getTodos({ assigned_to, status: "uncompleted" });
      if (result.success && result.data) {
        let todosWithLinks = result.data.map((todo) => {
          const appointment_id = todo.client_appointment_id;
          let link = `/admin/client-appointment?id=${appointment_id}`;
          let ca = clientAppointments.find((ca) => ca.id === appointment_id);
          if (historyClientAppointments.filter((ca) => ca.id === appointment_id).length > 0) {
            link = `/admin/history-client-appointment?id=${appointment_id}`;
            ca = historyClientAppointments.find((ca) => ca.id === appointment_id);
          } else if (archivedClientAppointments.filter((ca) => ca.id === appointment_id).length > 0) {
            link = `/admin/archived-client-appointment?id=${appointment_id}`;
            ca = archivedClientAppointments.find((ca) => ca.id === appointment_id);
          }

          let todoData = { ...todo };
          if (ca && !todoData.client_full_name) {
            todoData.client_full_name = `${_.get(ca, "client.firstName", "")} ${_.get(ca, "client.lastName", "")}`;
          }
          const deriveSpeciality = (caObj) => {
            if (!caObj) return "";
            // find profile by practitioner.practitionerId
            const practitionerId = _.get(caObj, "practitioner.practitionerId", null);
            if (practitionerId && Array.isArray(practitionerProfiles)) {
              return (
                practitionerProfiles.find(({ id }) => ca.practitioner.practitionerId === id)?.specialties?.[0]?.name ||
                ""
              );
            }
            return "";
          };

          todoData.client_speciality = deriveSpeciality(ca);

          return { ...todoData, link: link };
        });
        setTodos(todosWithLinks);
        // build specialty options for filter
        const specialties = _.uniq(todosWithLinks.map((t) => t.client_speciality).filter((s) => !!s));
        setSpecialtyOptions(specialties.map((s) => ({ label: s, value: s })));
      } else {
        console.error("Error fetching todos:", result.error);
        setTodos([]);
      }
    },
    [clientAppointments, historyClientAppointments, archivedClientAppointments, practitionerProfiles]
  );

  useEffect(() => {
    async function fetchUsers() {
      const users = await getUsers(ADMIN_ROLE);
      setAllUsers(users || []);
      setUsers(users?.filter((user) => !user.isITStaff && !user.disabled) || []);
    }

    fetchUsers();
  }, []);

  useEffect(() => {
    if (currentUser) {
      const currentViewableUsers = getUsersWithViewableTodos(currentUser, users, SCOPE.VIEW_ALL_TODOS);
      setSharedUsers(currentViewableUsers);
    }
  }, [currentUser, users]);

  // Links are built from ARCHIVED and HISTORY, so wait for them; a reply built
  // from empty lists could otherwise land after the right one.
  useEffect(() => {
    if (assigneeId && isOtherClientAppointmentsLoaded) {
      fetchTodos(assigneeId);
    }
  }, [assigneeId, fetchTodos, isOtherClientAppointmentsLoaded]);

  // ensure specialtyFilter only contains values present in specialtyOptions
  useEffect(() => {
    if (!specialtyOptions || specialtyOptions.length === 0) {
      setSpecialtyFilter([]);
      return;
    }
    const avail = new Set(specialtyOptions.map((o) => o.value));
    setSpecialtyFilter((prev) => (Array.isArray(prev) ? prev.filter((v) => avail.has(v)) : []));
  }, [specialtyOptions]);

  const handleSpecialtyFilterChange = (e) => {
    if (e && e.target && e.target.selectedOptions) {
      const selected = Array.from(e.target.selectedOptions)
        .map((o) => o.value)
        .filter((v) => v);
      setSpecialtyFilter(selected);
    } else {
      setSpecialtyFilter([]);
    }
  };

  const displayedTodos = useMemo(() => {
    let list = todos || [];
    if (specialtyFilter && specialtyFilter.length > 0) {
      list = list.filter((t) => specialtyFilter.includes(t.client_speciality));
    }
    return list;
  }, [todos, specialtyFilter]);

  const onDeleteTodo = async (id) => {
    const result = await deleteTodo(id);
    if (result.success) {
      fetchTodos(assigneeId);
    } else {
      console.error("Error deleting todo: ", result.error);
    }
  };

  return (
    <div className="todos-container">
      {sharedUsers.length > 1 && (
        <div className="level-left mb-1" style={{ alignItems: "baseline" }}>
          <SelectField
            label="Choose a shared assignee"
            onChange={handleSelectAssignee}
            value={assigneeId}
            options={sharedUsers.map((o) => ({
              label: `${o.firstName} ${o.lastName}`,
              value: o.uid,
            }))}
          />
          {specialtyOptions.length > 0 && (
            <div className="level-left mb-1" style={{ marginLeft: 24 }}>
              <SelectField
                label="Filter by Specialty"
                onChange={handleSpecialtyFilterChange}
                value={specialtyFilter}
                options={specialtyOptions}
              />
            </div>
          )}
        </div>
      )}
      {/* {isPermit(currentUser?.role, SCOPE.VIEW_ALL_TODOS) && (
        <div className="level-left mb-1">
          <SelectField
            label="Choose an assignee"
            onChange={handleSelectAssignee}
            value={assigneeId}
            options={users.map((o) => ({
              label: `${o.firstName} ${o.lastName}`,
              value: o.uid,
            }))}
          />
        </div>
      )} */}
      {isOtherClientAppointmentsLoaded ? (
        <TodosTable
          readOnly={true}
          todos={displayedTodos}
          selectedId={null}
          assignees={users}
          users={allUsers}
          onEdit={() => {}}
          onComplete={() => {}}
          onDelete={onDeleteTodo}
        />
      ) : (
        <WorkflowsLoading />
      )}
    </div>
  );
};

export default connect(mapStateToProps)(MyTodoList);
